/**
 * "What TDIA checked", the second tab of the Findings page (DEC-189, amends DEC-164).
 *
 * **The panel is the product; findings are the flags on it.** A lab that returned only
 * your abnormal values would be useless, because you could not tell whether the marker you
 * care about was measured and normal or simply never ordered. This tab is the full panel.
 *
 * It replaced `CoveragePanelCard`, which listed all ~96 checks in the narrow left column
 * and repeated every agent's warnings under every one of its checks. Four rules, each of
 * which stops a familiar mistake:
 *
 *   1. **The sentence adds up.** "F found something, C were clean, R could not run" sums
 *      to the headline; what did not apply and what is not built are named after it and
 *      never mixed into it (the old line mixed the populations).
 *   2. **`clean` and `not_run` never look alike.** One is assurance, the other is a hole.
 *   3. **Nothing here is a severity** (DEC-068). Found is orange, clean is green, could
 *      not run is amber: verdict colours, from the badge and mark tokens.
 *   4. **What TDIA does not check is shown, not hidden** (DEC-164 rule 3), collapsed.
 */
import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'

import type { CheckVerdict, CoveragePanelSummary, NotExaminedSummary } from '../../types/api'
import { TonePill, type PillTone } from '../shared/Badge'
import { NotExaminedCard } from './NotExaminedCard'
import { agentCategories, agentName, checkedSentence, plural } from './checkedModel'

type Verdict = CheckVerdict['verdict']

/** Bar segments. Verdict colours, not severity colours (DEC-068). */
const BAR_FILL: Record<Exclude<Verdict, 'not_implemented'>, string> = {
  findings: 'bg-mark-high',
  clean: 'bg-mark-success',
  not_run: 'bg-mark-warn',
  not_applicable: 'bg-hairline-strong',
}

/** The one pill style (DEC-193): the dot matches the bar segment beside it. */
const PILL: Record<Exclude<Verdict, 'not_implemented'>, PillTone> = {
  findings: 'high',
  clean: 'success',
  not_run: 'warn',
  not_applicable: 'stopped',
}

const PILL_LABEL: Record<Exclude<Verdict, 'not_implemented' | 'findings'>, string> = {
  clean: 'clean',
  not_run: 'could not run',
  not_applicable: 'did not apply',
}

const ROW_ORDER: Exclude<Verdict, 'not_implemented'>[] = [
  'findings', 'clean', 'not_run', 'not_applicable',
]

function countOf(checks: CheckVerdict[], verdict: Verdict): number {
  return checks.filter((c) => c.verdict === verdict).length
}

function Bar({ checks, verdicts, className }: {
  checks: CheckVerdict[]
  verdicts: Exclude<Verdict, 'not_implemented'>[]
  className: string
}) {
  return (
    <span className={`flex overflow-hidden rounded-full gap-[2px] ${className}`} aria-hidden>
      {verdicts.map((v) => {
        const n = countOf(checks, v)
        if (n === 0) return null
        return (
          <span
            key={v}
            data-verdict={v}
            className={`h-full ${BAR_FILL[v]}`}
            style={{ flex: `${n} 1 0` }}
          />
        )
      })}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Could not run
// ---------------------------------------------------------------------------

function CouldNotRunBox({ checks }: { checks: CheckVerdict[] }) {
  // Grouped by reason: the checks of one skipped agent share it, and repeating a
  // two-line reason under each of them is the repetition this tab exists to remove.
  const groups: Array<{ reason: string; checks: CheckVerdict[] }> = []
  for (const check of checks) {
    const group = groups.find((g) => g.reason === check.reason)
    if (group) group.checks.push(check)
    else groups.push({ reason: check.reason, checks: [check] })
  }

  return (
    <section
      aria-label="Could not run"
      className="rounded-lg bg-chip-warn border border-warn/30 px-4 py-3 flex flex-col gap-[6px]"
    >
      <h3 className="text-[13px] font-semibold text-warn">Could not run</h3>
      {checks.length === 0 ? (
        <p className="text-[13px] text-muted leading-[1.5]">Every check that applied ran.</p>
      ) : (
        groups.map((group) => (
          <div key={group.reason} className="flex flex-col gap-[3px]">
            {group.checks.map((c) => (
              <p key={c.check_id} className="text-[13px] text-body">
                {c.name}
                <span className="text-muted"> · {c.agent_id ? agentName(c.agent_id) : 'No agent'}</span>
              </p>
            ))}
            {group.reason && (
              <p className="text-[12px] text-muted leading-[1.45]">Reason: {group.reason}</p>
            )}
          </div>
        ))
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// One row per agent
// ---------------------------------------------------------------------------

interface AgentRowProps {
  agentId: string
  checks: CheckVerdict[]
  notes: string[]
  findingCount: number
  onSeeFindings: (agentId: string) => void
}

function agentSummary(checks: CheckVerdict[]): string {
  const parts: string[] = []
  const found = countOf(checks, 'findings')
  const clean = countOf(checks, 'clean')
  const notRun = countOf(checks, 'not_run')
  const notApplicable = countOf(checks, 'not_applicable')
  if (found) parts.push(`${found} found`)
  if (clean) parts.push(`${clean} clean`)
  if (notRun) parts.push(`${notRun} could not run`)
  if (notApplicable) parts.push(`${notApplicable} did not apply`)
  return parts.join(' · ')
}

function CheckLine({ check }: { check: CheckVerdict }) {
  const verdict = check.verdict as Exclude<Verdict, 'not_implemented'>
  const label = verdict === 'findings' ? `${check.count} found` : PILL_LABEL[verdict]
  const showReason = (verdict === 'not_run' || verdict === 'not_applicable') && check.reason
  // Assurance has to be checkable: "0 of 412 python files" is evidence a reader can
  // dispute, "clean" alone is a claim (DEC-164).
  const denominator =
    verdict === 'clean' && check.denominator !== null
      ? `0 of ${check.denominator} ${check.denominator_unit}`
      : ''

  return (
    // The result sits in a fixed first column (DEC-193 decision 3), so every check's
    // name starts at the same place whatever the result's length.
    <li className="grid grid-cols-[150px_minmax(0,1fr)] gap-3 items-center px-3 py-[7px] rounded-md bg-fill-subtle">
      <TonePill tone={PILL[verdict]}>{label}</TonePill>
      <span className="flex flex-col gap-[2px] min-w-0">
        <span className="text-[14px] text-ink">{check.name}</span>
        {showReason && <span className="text-[13px] text-muted">{check.reason}</span>}
        {denominator && <span className="text-[13px] tabular-nums text-faint">{denominator}</span>}
      </span>
    </li>
  )
}

function AgentRow({ agentId, checks, notes, findingCount, onSeeFindings }: AgentRowProps) {
  const [open, setOpen] = useState(false)
  const name = agentName(agentId)
  const ordered = ROW_ORDER.flatMap((v) => checks.filter((c) => c.verdict === v))

  return (
    <li className="border-b border-hairline last:border-b-0" data-testid={`agent-row-${agentId}`}>
      <div className="flex items-center gap-3 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex-1 min-w-0 grid grid-cols-[16px_150px_minmax(0,1fr)_auto] gap-3 items-center text-left rounded-md focus:outline-none focus-visible:shadow-focus"
        >
          {open
            ? <ChevronDown className="w-3.5 h-3.5 text-faint" />
            : <ChevronRight className="w-3.5 h-3.5 text-faint" />}
          <span className="text-[15px] font-semibold text-ink truncate">{name}</span>
          <Bar
            checks={checks}
            verdicts={ROW_ORDER}
            className="h-[6px] max-w-[300px] bg-fill-subtle"
          />
          <span className="text-[13px] text-body whitespace-nowrap">{agentSummary(checks)}</span>
        </button>
        {findingCount > 0 && (
          <button
            type="button"
            onClick={() => onSeeFindings(agentId)}
            className="shrink-0 px-[11px] py-[5px] rounded-md border border-high/50 bg-chip-high text-high text-[12px] font-semibold whitespace-nowrap hover:border-high focus:outline-none focus-visible:shadow-focus"
          >
            See its {findingCount} {plural(findingCount, 'finding', 'findings')} →
          </button>
        )}
      </div>
      {open && (
        <div className="pl-8 pb-[14px] flex flex-col gap-[6px]">
          {/* The agent's warnings, once (DEC-189). Copied onto each check they read as
              if they were about that check, which most were not. */}
          {notes.length > 0 && (
            <div
              data-testid={`agent-notes-${agentId}`}
              className="text-[12px] text-warn px-3 py-2 rounded-md bg-chip-warn flex flex-col gap-[3px]"
            >
              {notes.map((note, i) => (
                <span key={`${i}-${note}`}>{note}</span>
              ))}
            </div>
          )}
          <ul className="flex flex-col gap-[6px]">
            {ordered.map((check) => (
              <CheckLine key={check.check_id} check={check} />
            ))}
          </ul>
        </div>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

interface CheckedTabProps {
  /** Optional: a report from a backend that predates the panel is the not-recorded case. */
  panel?: CoveragePanelSummary
  notExamined: NotExaminedSummary
  /** Findings per category, from the report summary, for "See its N findings". */
  findingsByCategory: Record<string, number>
  onSeeFindings: (agentId: string) => void
}

export function CheckedTab({ panel, notExamined, findingsByCategory, onSeeFindings }: CheckedTabProps) {
  const [notBuiltOpen, setNotBuiltOpen] = useState(false)

  if (!panel || panel.coverage_not_recorded) {
    return (
      <div className="flex flex-col gap-[14px]">
        <div className="bg-panel border border-hairline rounded-lg shadow-card px-[22px] py-5">
          {/* Never an empty panel: that would be a coverage statement this scan never made. */}
          <p className="text-[14px] text-body leading-[1.5]">
            Coverage was not recorded for this scan. It ran before TDIA began storing what
            each check could see, so this tab cannot say what was examined.
          </p>
        </div>
        <NotExaminedCard notExamined={notExamined} />
      </div>
    )
  }

  const built = panel.checks.filter((c) => c.verdict !== 'not_implemented')
  const notBuilt = panel.checks.filter((c) => c.verdict === 'not_implemented')
  const found = countOf(built, 'findings')
  const clean = countOf(built, 'clean')
  const notRun = countOf(built, 'not_run')
  const notApplicable = countOf(built, 'not_applicable')
  const applicable = found + clean + notRun

  // One row per agent, in the order the registry lists their checks.
  const agentIds: string[] = []
  for (const c of built) {
    if (c.agent_id && !agentIds.includes(c.agent_id)) agentIds.push(c.agent_id)
  }
  const notes = panel.agent_notes ?? {}

  const legend = [
    { label: 'Found something', n: found, fill: BAR_FILL.findings },
    { label: 'Clean', n: clean, fill: BAR_FILL.clean },
    { label: 'Could not run', n: notRun, fill: BAR_FILL.not_run },
  ]

  return (
    <div className="flex flex-col gap-[14px]">
      <div className="bg-panel border border-hairline rounded-lg shadow-card px-[22px] py-5 flex flex-col gap-[14px]">
        <div className="flex justify-between items-end gap-4 flex-wrap">
          <div className="flex flex-col gap-[6px]">
            <h2 className="text-[20px] font-semibold text-ink">
              {applicable} {plural(applicable, 'check', 'checks')} applied to this repository
            </h2>
            <p className="text-[14px] text-body" data-testid="checked-sentence">
              {checkedSentence({ found, clean, notRun, notApplicable, notBuilt: notBuilt.length })}
            </p>
          </div>
          <div className="flex gap-[18px] flex-wrap">
            {legend.map((l) => (
              <span key={l.label} className="inline-flex items-center gap-2 text-[13px] text-body">
                <span className={`w-[10px] h-[10px] rounded-[3px] ${l.fill}`} aria-hidden />
                <span className="text-[15px] font-semibold tabular-nums text-ink">{l.n}</span>
                {l.label}
              </span>
            ))}
          </div>
        </div>
        <Bar checks={built} verdicts={['findings', 'clean', 'not_run']} className="h-[10px]" />
        <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-[10px] items-start">
          <CouldNotRunBox checks={built.filter((c) => c.verdict === 'not_run')} />
          <NotExaminedCard notExamined={notExamined} />
        </div>
      </div>

      <div className="bg-panel border border-hairline rounded-lg shadow-card px-[22px] py-[6px]">
        <ul>
          {agentIds.map((agentId) => (
            <AgentRow
              key={agentId}
              agentId={agentId}
              checks={built.filter((c) => c.agent_id === agentId)}
              notes={notes[agentId] ?? []}
              findingCount={agentCategories(agentId).reduce(
                (sum, cat) => sum + (findingsByCategory[cat] ?? 0),
                0,
              )}
              onSeeFindings={onSeeFindings}
            />
          ))}
        </ul>
        {notBuilt.length > 0 && (
          <div className="py-3 border-t border-hairline">
            <button
              type="button"
              onClick={() => setNotBuiltOpen((v) => !v)}
              aria-expanded={notBuiltOpen}
              className="flex items-center gap-2 text-left rounded-md focus:outline-none focus-visible:shadow-focus"
            >
              {notBuiltOpen
                ? <ChevronDown className="w-3.5 h-3.5 text-faint" />
                : <ChevronRight className="w-3.5 h-3.5 text-faint" />}
              <span className="text-[15px] font-semibold text-body">Not built yet</span>
              <span className="text-[13px] text-faint">
                · {notBuilt.length} {plural(notBuilt.length, 'check', 'checks')} TDIA does
                not do yet, shown on purpose
              </span>
            </button>
            {notBuiltOpen && (
              <ul
                data-testid="not-built-list"
                className="pt-[10px] pl-[26px] grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-[6px]"
              >
                {notBuilt.map((c) => (
                  <li
                    key={c.check_id}
                    title={c.reason || undefined}
                    className="text-[12px] text-muted px-3 py-[7px] rounded-md border border-dashed border-hairline-strong"
                  >
                    {c.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
